/**
 * VolumeControls - UI component for adjusting audio volumes
 * Provides sliders for Ambience, Music, and SFX categories
 */

import { useState } from 'react';
import { useAudio } from '../../hooks/useAudio.js';
import { Card } from './Card.jsx';

export function VolumeControls() {
  const audio = useAudio();
  const [isOpen, setIsOpen] = useState(false);

  const handleVolumeChange = (category, value) => {
    audio.setVolume(category, value);
  };

  const handleMuteToggle = () => {
    audio.setMuted(!audio.muted);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          audio.playSound('ui_open');
          setIsOpen(true);
        }}
        className="fixed top-4 right-4 p-3 bg-amber-700 hover:bg-amber-800 text-white rounded-full shadow-lg transition-all transform hover:scale-110 z-50"
        aria-label="Open volume controls"
        title="Volume Settings"
      >
        {audio.muted ? '🔇' : '🔊'}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="max-w-md w-full">
        <Card title="Volume Settings" icon="🔊">
          <div className="space-y-4">
            {/* Master Volume */}
            <div>
              <label className="flex justify-between items-center mb-2">
                <span className="font-bold text-amber-900">Master Volume</span>
                <span className="text-sm text-amber-700">
                  {Math.round(audio.volumes.master * 100)}%
                </span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={audio.volumes.master}
                onChange={(e) => handleVolumeChange('master', parseFloat(e.target.value))}
                className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-amber-700"
              />
            </div>

            {/* Ambience Volume */}
            <div>
              <label className="flex justify-between items-center mb-2">
                <span className="font-bold text-amber-900">🌲 Ambience</span>
                <span className="text-sm text-amber-700">
                  {Math.round(audio.volumes.ambience * 100)}%
                </span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={audio.volumes.ambience}
                onChange={(e) => handleVolumeChange('ambience', parseFloat(e.target.value))}
                className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
            </div>

            {/* Music Volume */}
            <div>
              <label className="flex justify-between items-center mb-2">
                <span className="font-bold text-amber-900">🎵 Music</span>
                <span className="text-sm text-amber-700">
                  {Math.round(audio.volumes.music * 100)}%
                </span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={audio.volumes.music}
                onChange={(e) => handleVolumeChange('music', parseFloat(e.target.value))}
                className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>

            {/* SFX Volume */}
            <div>
              <label className="flex justify-between items-center mb-2">
                <span className="font-bold text-amber-900">🎲 Sound Effects</span>
                <span className="text-sm text-amber-700">
                  {Math.round(audio.volumes.sfx * 100)}%
                </span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={audio.volumes.sfx}
                onChange={(e) => handleVolumeChange('sfx', parseFloat(e.target.value))}
                className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-red-600"
              />
            </div>

            {/* Mute Toggle */}
            <div className="pt-4 border-t border-amber-300">
              <button
                onClick={() => {
                  audio.playSound('ui_confirm');
                  handleMuteToggle();
                }}
                className={`w-full py-3 px-6 rounded-xl font-bold border-2 shadow-lg transition-all ${
                  audio.muted
                    ? 'bg-red-700 hover:bg-red-800 text-red-50 border-red-900'
                    : 'bg-green-700 hover:bg-green-800 text-green-50 border-green-900'
                }`}
              >
                {audio.muted ? '🔇 Unmute All' : '🔊 Mute All'}
              </button>
            </div>

            {/* Close Button */}
            <div className="text-center pt-2">
              <button
                onClick={() => {
                  audio.playSound('ui_close');
                  setIsOpen(false);
                }}
                className="px-6 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
